/**
 * Permission Grid (piece 4): rows, columns and a boolean cell, for the role
 * editor's module × read/write/edit/delete grid and decision 0068's role ×
 * document class grid. A whole row and a whole column toggle at once.
 */
export type PermissionMatrix = ReadonlyMap<string, ReadonlySet<string>>;

export function cellOn(matrix: PermissionMatrix, row: string, column: string): boolean {
  return matrix.get(row)?.has(column) ?? false;
}

export function toggleCell(matrix: PermissionMatrix, row: string, column: string): Map<string, Set<string>> {
  const next = cloneMatrix(matrix);
  const set = next.get(row) ?? new Set<string>();
  if (set.has(column)) set.delete(column);
  else set.add(column);
  next.set(row, set);
  return next;
}

export function rowAllOn(matrix: PermissionMatrix, row: string, columns: readonly string[]): boolean {
  return columns.length > 0 && columns.every((c) => cellOn(matrix, row, c));
}

export function columnAllOn(matrix: PermissionMatrix, rows: readonly string[], column: string): boolean {
  return rows.length > 0 && rows.every((r) => cellOn(matrix, r, column));
}

/** A row toggles to all on unless it already is, in which case to all off. */
export function toggleRow(matrix: PermissionMatrix, row: string, columns: readonly string[]): Map<string, Set<string>> {
  const next = cloneMatrix(matrix);
  next.set(row, rowAllOn(matrix, row, columns) ? new Set() : new Set(columns));
  return next;
}

export function toggleColumn(matrix: PermissionMatrix, rows: readonly string[], column: string): Map<string, Set<string>> {
  const next = cloneMatrix(matrix);
  const turnOn = !columnAllOn(matrix, rows, column);
  for (const row of rows) {
    const set = next.get(row) ?? new Set<string>();
    if (turnOn) set.add(column);
    else set.delete(column);
    next.set(row, set);
  }
  return next;
}

function cloneMatrix(matrix: PermissionMatrix): Map<string, Set<string>> {
  return new Map([...matrix.entries()].map(([r, cols]) => [r, new Set(cols)]));
}
